<template>
    <vx-filterable :active="active" @toggle="toggleFilterable">
        <template v-slot:placeholder>
            <span class="mr-2">Search</span>
            <vx-badge v-if="filtersCount > 0" :borders="false" size="xs" @click.stop="reset">
                {{ pluralized('filter', filtersCount, true )}}
                <font-awesome-icon widthAuto :icon="faCircleXmark" />
            </vx-badge>
        </template>
        <template v-slot:form>
            <form @submit.prevent="apply" class="space-y-4">
                <div class="space-y-1">
                    <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                        Keyword Search
                    </filter-label>

                    <vx-input
                        type="text"
                        data-test="filters-query"
                        placeholder="Search"
                        maxlength="120"
                        size="lg"
                        v-model="filters.query">
                        <template v-slot:right>
                            <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                        </template>
                    </vx-input>
                </div>

                <div>
                    <filter-label :canReset="filters.category != undefined" @reset="filters.category = undefined">
                        Category
                    </filter-label>
                    <category-filter-select v-model="filters.category" size="lg"></category-filter-select>
                </div>

                <in-flow-filterable-item v-if="FEATURES.flows"
                    v-model="filters.flow"
                ></in-flow-filterable-item>

                <div class="flex gap-2 pt-4">
                    <vx-button type="button" size="lg" color="muted-light" @click="cancel">Cancel</vx-button>
                    <vx-button data-test="filters-apply" size="lg" type="submit" block>
                        Apply
                        <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                    </vx-button>
                </div>
            </form>
        </template>
    </vx-filterable>
</template>

<script setup>
import { computed, ref, inject, watch, nextTick } from 'vue';
import { VxBadge, VxButton, VxFilterable, VxInput } from '@voxie/frontend-components';
import { faCircleCheck, faMagnifyingGlass, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import InFlowFilterableItem from '~/components/automation/flows/components/InFlowFilterableItem.vue';
import CategoryFilterSelect from '~/components/general/categories/CategoryFilterSelect.vue';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';

const FEATURES = inject('FEATURES');

const filters = ref({})
const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
});
watch(() => props.modelValue, (modelValue) => {
    filters.value = {...modelValue};
}, {
    deep: true,
    immediate: true,
})
const emit = defineEmits(['update:modelValue', 'apply', 'reset']);

const active = ref(false);

const toggleFilterable = (event) => {
    active.value = event;
}

const isFlowFilterApplied = computed(() => {
    if (!props.modelValue.flow) {
        return false;
    }

    return props.modelValue.flow.in_flow !== undefined || !!(props.modelValue.flow.flow_id)
});
const filtersCount = computed(
    () =>
        (props.modelValue.query ? 1 : 0) +
        (isFlowFilterApplied.value ? 1 : 0) +
        (props.modelValue.category?.length ? 1 : 0)
);

const reset = () => {
    active.value = false;
    emit('reset');
}
const cancel = async () => {
    filters.value = { ...props.modelValue }
    active.value = false;
}

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
}

</script>
